if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.bindViewerNavToThumbnails();
        requestAnimationFrame(() => this.syncViewerArrowDisabled());
      }

      bindViewerNavToThumbnails() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        const prevBtn = viewer.querySelector('button[name="previous"]');
        const nextBtn = viewer.querySelector('button[name="next"]');
        if (!prevBtn || !nextBtn) return;
        prevBtn.addEventListener('click', this.onViewerArrowClick.bind(this, -1), { capture: true });
        nextBtn.addEventListener('click', this.onViewerArrowClick.bind(this, 1), { capture: true });
      }

      onViewerArrowClick(delta, event) {
        const thumbRoot = this.elements.thumbnails;
        if (!thumbRoot) return;

        const items = Array.from(thumbRoot.querySelectorAll('[data-target]'));
        if (items.length < 2) return;

        const currentBtn = thumbRoot.querySelector('button[aria-current="true"]');
        const currentLi = currentBtn?.closest('[data-target]');
        if (!currentLi) return;

        const idx = items.indexOf(currentLi);
        if (idx < 0) return;

        const nextIdx = idx + delta;
        if (nextIdx < 0 || nextIdx >= items.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.setActiveMedia(items[nextIdx].dataset.target, false);
      }

      syncViewerArrowDisabled() {
        const viewer = this.elements.viewer;
        const thumbRoot = this.elements.thumbnails;
        if (!viewer || !thumbRoot) return;

        const prevBtn = viewer.querySelector('button[name="previous"]');
        const nextBtn = viewer.querySelector('button[name="next"]');
        if (!prevBtn || !nextBtn) return;

        const items = thumbRoot.querySelectorAll('[data-target]');
        if (items.length < 2) {
          prevBtn.setAttribute('disabled', 'disabled');
          nextBtn.setAttribute('disabled', 'disabled');
          return;
        }

        const currentBtn = thumbRoot.querySelector('button[aria-current="true"]');
        const currentLi = currentBtn?.closest('[data-target]');
        const idx = currentLi ? Array.from(items).indexOf(currentLi) : 0;

        if (idx <= 0) prevBtn.setAttribute('disabled', 'disabled');
        else prevBtn.removeAttribute('disabled');

        if (idx >= items.length - 1) nextBtn.setAttribute('disabled', 'disabled');
        else nextBtn.removeAttribute('disabled');
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.syncViewerArrowDisabled();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const list = activeMedia.parentElement;
          if (!this.mql.matches && this.elements.thumbnails) {
            list.scrollLeft = 0;
          } else if (!this.mql.matches || this.elements.thumbnails) {
            list.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.syncViewerArrowDisabled();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
